import { useMedication } from '@hook'
import { Select } from '@ui/SearchInput/Select'
import { useCustomOptionStore } from '@zustore/store'
import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

const CUSTOM_FREQUENCY = '__custom__'

type Option = { label: string; value: string }

const toOption = (value: string, options: Option[]): Option | null =>
  value ? options.find(option => option.value === value) || { label: value, value } : null

interface EditableOptionFieldProps {
  name: string
  label: string
  options: string[]
  placeholder?: string
}

/** A form-bound dropdown whose value can be typed over or deleted like text. */
export const EditableOptionField = ({ name, label, options, placeholder }: EditableOptionFieldProps) => {
  const { control } = useFormContext()
  const selectOptions = useMemo(() => options.map(option => ({ label: option, value: option })), [options])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Select
          borderRadius="25px"
          className="capitalize"
          error={error?.message}
          isEditable
          label={label}
          onChange={(option: any) => onChange(option?.value ?? '')}
          options={selectOptions}
          placeholder={placeholder}
          value={toOption(value, selectOptions)}
        />
      )}
    />
  )
}

interface FrequencySelectProps {
  label: string
  /** Display text of the current frequency: a standard frequency name or a rendered custom one. */
  value: string
  onSelect: (frequencyName: string) => void
  onCustom: () => void
  onClear: () => void
}

/** Standard frequencies plus "Custom…" (which opens the custom-frequency modal), editable like text. */
export const FrequencySelect = ({ label, value, onSelect, onCustom, onClear }: FrequencySelectProps) => {
  const { medFrequencies = [] } = useMedication()
  const { setIsCustomFrequencySelected } = useCustomOptionStore()

  const options = useMemo<Option[]>(
    () => [
      ...medFrequencies.map(frequency => ({
        label: `${frequency?.attributes?.code} — ${frequency?.attributes?.name}`,
        value: frequency?.attributes?.name
      })),
      { label: 'Custom…', value: CUSTOM_FREQUENCY }
    ],
    [medFrequencies]
  )

  const handleChange = (option: Option | null) => {
    if (!option) {
      onClear()
      return
    }
    if (option.value === CUSTOM_FREQUENCY) {
      setIsCustomFrequencySelected(true)
      onCustom()
      return
    }
    setIsCustomFrequencySelected(false)
    onSelect(option.value)
  }

  return (
    <Select
      borderRadius="25px"
      isEditable
      label={label}
      onChange={handleChange as any}
      options={options}
      placeholder="Select frequency"
      value={toOption(value, options)}
    />
  )
}
